/**
 * Адреса экранов, которые НЕ являются адресами уровней.
 *
 * Разделение с `levels.ts` намеренное и проходит по одному вопросу: адресует
 * ли путь ЕДИНИЦУ УЧЁТА. Район, массив, хозяйство и контур — единицы, их путь
 * собирает `levelPath()`, и другого источника у него быть не должно. Кабинет
 * хозяйства единиц не заводит: «Сегодня», «Мои поля» и «Мои отчёты» — три
 * вопроса об ОДНОМ хозяйстве, то есть три экрана внутри уровня `actor`, а не
 * три новых уровня. Поэтому они живут здесь, а правило уровней не меняется.
 *
 * Зачем константы вообще. Путь, написанный строкой в разметке, повторяется
 * столько раз, сколько на него ссылок, и расходится молча: переименование
 * чинит четыре места из пяти, а пятое ведёт в «страницу не найдена» — причём
 * только у той роли, у которой этот переход и есть работа.
 */

export const ROUTES = {
  /** Уровень района. Дублирует `levelPath("district")` по значению, но не по
      смыслу: сюда ссылаются как на ДОМ роли, а не как на уровень. */
  district: "/",

  /* -- кабинет хозяйства: три адреса вместо одного с параметром -------- */
  //
  // Три вопроса хозяйства разные, и один экран с вкладками отвечал бы на них
  // по очереди, теряя адрес: ссылку «мои поля» некуда было бы дать.
  /** Что от хозяйства ждут прямо сейчас. */
  myToday: "/my/today",
  /** Земля хозяйства: контуры и гектары. */
  myFields: "/my/fields",
  /** Отчёты хозяйства и суждения проверки по ним. */
  myHistory: "/my/history",

  /**
   * Ввод за хозяйство районным аккаунтом.
   *
   * Остаётся отдельным адресом с `?actor_id=`: это работа ДИСПЕТЧЕРА за чужое
   * хозяйство, а не кабинет своего. Слить их значило бы назвать «моими» чужие
   * поля и чужие отчёты.
   */
  farmWorks: "/my-works",

  actors: "/actors",
  verification: "/verification",
  /**
   * Очередь ситуаций: к кому ехать и что по этому уже решили.
   *
   * Своей единицы учёта у неё нет — поэтому она здесь, а не в `levelPath()`:
   * строка очереди адресует НАБЛЮДЕНИЕ о хозяйстве на дату, а не хозяйство.
   * Переход по имени хозяйства из строки ведёт на его уровень (`levelPath`), и
   * это не то же самое, что фильтр этого же экрана.
   */
  situations: "/situations",
  campaigns: "/campaigns",
  /** Карта района: единственный вид, у которого нет своей единицы учёта —
      поэтому она здесь, а не в `levelPath()`. */
  map: "/map",
  /** Обучение на живых данных среза. */
  guide: "/guide",

  /**
   * Станция ответов: вопрос инструментом реестра и журнал заданных вопросов.
   *
   * Своей единицы учёта у неё нет — поэтому она здесь, а не в `levelPath()`:
   * станция стоит не НА уровне, а рядом со всеми уровнями сразу. Карман
   * уровня отвечает на вопрос об одном экране и адреса не заводит.
   */
  analytics: "/analytics",
} as const;

export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];
